import * as api from './middleware/api.middleware';
import {
    IMessageList,
    IMessage,
    IGetMessagesRequest,
    IMoveMessagesRequest,
    IMessageNote,
    IUnreadCount,
    IMessageRead,
    IMessagesRead,
    IMessagesAddRemoveFlag,
    IMailboxMessagesCount,
    IMessageDeleteRequest,
    IMessagesLegalNotify,
    IGetMessagesResponse,
    IUpdateMessageRequest,
    IMessageContactInfo,
    IUpdateMessageActiveContactPointRequest,
} from '../interfaces/message.interfaces';
import { getMiddlewareOptionsWithContext } from './middleware/helpers';
import { ErrorContext, RecipientIdType } from '../enums';
import { IBatchResponse } from '../interfaces/general.interfaces';
const serviceUrl = '/messageservice/v2';

export function getMessagesByIds(mailboxId: string, messageIds: string[]) {
    return api.post<IMessage[]>(`${serviceUrl}/${mailboxId}/messages/byids`, messageIds);
}

export function getMessages(request: IGetMessagesRequest) {
    return api.post<IGetMessagesResponse>(serviceUrl + '/messages/messagelist', request);
}

export function getMessage(mailboxId: string, messageId: string, languageId: string, context?: ErrorContext) {
    const url = serviceUrl + `/${mailboxId}/messages/${messageId}?languageId=${languageId}`;
    return api.get<IMessage>(url, null, getMiddlewareOptionsWithContext(context));
}

export function moveMessages(data: IMoveMessagesRequest, context?: ErrorContext) {
    const url = serviceUrl + `/${data.mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(
        url,
        data.messagesToMove,
        null,
        getMiddlewareOptionsWithContext(context)
    );
}

export function updateNote(mailboxId: string, note: IMessageNote, context?: ErrorContext) {
    const noteData: IMessageNote[] = [{ id: note.id, version: note.version, note: note.note }];
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, noteData, null, getMiddlewareOptionsWithContext(context));
}

export const updateMessageRead = (
    mailboxId: string,
    messageId: string,
    read: IMessageRead,
    context?: ErrorContext
): Promise<IBatchResponse<IMessageList>> => {
    const messagesRead: IMessagesRead[] = [{ id: messageId, version: read.version, read: read.read }];
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, messagesRead, null, getMiddlewareOptionsWithContext(context));
};

export function updateMessagesRead(mailboxId: string, documents: IMessagesRead[], context?: ErrorContext) {
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, documents, null, getMiddlewareOptionsWithContext(context));
}

export function updateMessagesFlag(mailboxId: string, documents: IMessagesAddRemoveFlag[], context?: ErrorContext) {
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, documents, null, getMiddlewareOptionsWithContext(context));
}

export function updateMessages(mailboxId: string, documents: IUpdateMessageRequest[]) {
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, documents);
}

export function updateMessagesActiveContactPoint(
    mailboxId: string,
    documents: IUpdateMessageActiveContactPointRequest[]
) {
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, documents);
}

export function moveMessagesToDeletedFolder(data: IMoveMessagesRequest, context?: ErrorContext) {
    const url = serviceUrl + `/${data.mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(
        url,
        data.messagesToMove,
        null,
        getMiddlewareOptionsWithContext(context)
    );
}

export function deleteMessages(mailboxId: string, messagesToDelete: IMessageDeleteRequest[], context?: ErrorContext) {
    return api.del<IBatchResponse<any>>(
        serviceUrl + `/${mailboxId}/messages`,
        messagesToDelete,
        null,
        getMiddlewareOptionsWithContext(context)
    );
}

export function createMessage(mailboxId: string, language: string) {
    const url = serviceUrl + `/${mailboxId}/messages/?languageId=${language}`;
    return api.post<IMessage>(url, null);
}

export function replyMessage(mailboxId: string, messageId: string) {
    const url = serviceUrl + `/${mailboxId}/messages/${messageId}/reply`;
    return api.post<IMessage>(url, null);
}

export function sendMessage(mailboxId: string, messageId: string) {
    const url = serviceUrl + `/${mailboxId}/messages/${messageId}/send`;
    return api.post<IMessage>(url, null);
}

export function getAllUnreadMessagesCount(mailboxId: string) {
    const queryParams = ['mailboxId=' + mailboxId, '$groupby=folderId', '$count=id', 'read=false', 'trashed=false'];
    const url = serviceUrl + '/messages/unreadcount?' + queryParams.join('&');
    return api.get<IUnreadCount>(url);
}

export function getAmountOfAllMessages(mailboxId: string) {
    return api.get<IMailboxMessagesCount>(`${serviceUrl}/messages?mailboxId=${mailboxId}&$groupby=mailboxId&$count=id`);
}

export function updateMessageLegallyNotify(mailboxId: string, messagesRead: IMessagesLegalNotify[]) {
    const url = serviceUrl + `/${mailboxId}/messages/update`;
    return api.post<IBatchResponse<IMessageList>>(url, messagesRead);
}

export const createUpdateMessageRequest = (
    message: IMessage,
    subject: string,
    cvrNumber: string,
    contactPointId: string,
    contactGroup: string,
    label: string
): IUpdateMessageRequest => {
    return {
        id: message.id,
        version: message.version,
        label: subject,
        folderId: message.folderId,
        note: message.note,
        ...(subject !== null && {
            recipient: {
                id: message.recipient.id,
                version: message.recipient.version,
                recipientId: cvrNumber,
                recipientIdType: RecipientIdType.CVR,
                contactPointId: contactPointId,
                label: label,
                contactGroup: contactGroup,
            },
        }),
    };
};

export const createUpdateActiveContactPointMessageRequest = (
    message: IMessage,
    subject: string,
    cvrNumber: string,
    contactPointId: string,
    contactGroup: string,
    label: string,
    contactInfo: Array<IMessageContactInfo>
): IUpdateMessageActiveContactPointRequest => {
    return {
        id: message.id,
        version: message.version,
        label: subject,
        folderId: message.folderId,
        note: message.note,
        ...(subject !== null && {
            recipient: {
                id: message.recipient.id,
                version: message.recipient.version,
                recipientId: cvrNumber,
                recipientIdType: RecipientIdType.CVR,
                contactPointId: contactPointId,
                label: label,
                contactGroup: contactGroup,
                contactInfo: contactInfo,
            },
        }),
    };
};
